// Dynamic color palette that reads data-dark attribute from DOM
// Static objects in STRUCTURAL are automatically resolved for dark mode via DARK_REPLACEMENTS

const LIGHT_VALS = {
  pri: '#0B1A4A',
  pri2: '#2952CC',
  green: '#34a853',
  red: '#ea4335',
  amber: '#f59e0b',
  muted: '#6B7280',
  bg: '#F5F7FA',
  bg2: '#EDF0F5',
  white: '#FFFFFF',
  border: '#E8EAEF',
  text: '#1F2937',
  ink: '#0B1A4A',
  faint: '#9aa0a6',
  orange: '#ff6d00',
  blue: '#1a73e8',
  purple: '#7c3aed',
  card: '#FFFFFF',
  line: '#F0F2F5',
  surface: '#ffffff',
  surfaceAlt: '#f1f5f9',
  inputBg: '#f8fafc',
  greenBg: '#e6f4ea',
  redBg: '#fce8e6',
  amberBg: '#fef7e0',
  blueBg: '#e8f0fe',
  successBg: '#ecfdf5',
  dangerBg: '#fef2f2',
  warningBg: '#fffbeb',
  infoBg: '#eff6ff',
  successText: '#065f46',
  dangerText: '#991b1b',
  warningText: '#92400e',
  infoText: '#1e40af',
  errorBg: '#fef2f2',
  overlay: 'rgba(0,0,0,0.4)',
  canvasBg: '#e8eaee',
  acc: '#0B1A4A',
};

const DARK_VALS = {
  pri: '#e2e8f0',
  pri2: '#60a5fa',
  green: '#34d399',
  red: '#f87171',
  amber: '#fbbf24',
  muted: '#64748b',
  bg: '#0f172a',
  bg2: '#1e293b',
  white: '#1e293b',
  border: '#334155',
  text: '#e2e8f0',
  ink: '#e2e8f0',
  faint: '#475569',
  orange: '#fb923c',
  blue: '#60a5fa',
  purple: '#a78bfa',
  card: '#1e293b',
  line: '#334155',
  surface: '#1e293b',
  surfaceAlt: '#0f172a',
  inputBg: '#0f172a',
  greenBg: '#064e3b',
  redBg: '#7f1d1d',
  amberBg: '#78350f',
  blueBg: '#1e3a5f',
  successBg: '#064e3b',
  dangerBg: '#7f1d1d',
  warningBg: '#78350f',
  infoBg: '#1e3a5f',
  successText: '#6ee7b7',
  dangerText: '#fca5a5',
  warningText: '#fcd34d',
  infoText: '#93c5fd',
  errorBg: '#7f1d1d',
  overlay: 'rgba(0,0,0,0.7)',
  canvasBg: '#1e293b',
  acc: '#e2e8f0',
};

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

// Map hardcoded light hex values → dark hex values for STRUCTURAL object resolution
const DARK_REPLACEMENTS = {
  '#fff': '#1e293b',
  '#ffffff': '#1e293b',
  '#FFFFFF': '#1e293b',
  '#f9fafb': '#1e293b',
  '#fafbfc': '#1e293b',
  '#fafbfd': '#1e293b',
  '#f8f9fb': '#1e293b',
  '#111827': '#e2e8f0',
  '#9ca3af': '#64748b',
  '#6b7280': '#64748b',
  '#374151': '#cbd5e1',
  '#1e40af': '#93c5fd',
  '#2563eb': '#60a5fa',
  '#f3f4f6': '#334155',
  '#e5e7eb': '#334155',
  '#eef0f4': '#334155',
  '#eef0f6': '#334155',
  '#fecaca': '#7f1d1d',
  '#dc2626': '#f87171',
  '#ecfdf5': '#064e3b',
  '#059669': '#34d399',
  '#fffbeb': '#78350f',
  '#d97706': '#fbbf24',
  '#f5f3ff': '#2d1f6b',
  '#7c3aed': '#a78bfa',
  '#eff6ff': '#1e3a5f',
  '#eef3ff': '#1e3a5f',
  '#f0f4ff': '#1e3a5f',
  '#f0f9ff': '#1e3a5f',
  '#f1f3f4': '#334155',
  '#e8eaef': '#334155',
  '#fef2f2': '#7f1d1d',
  '#fee2e2': '#7f1d1d',
  '#fce8e6': '#7f1d1d',
  '#fdecea': '#7f1d1d',
  '#e6f4ea': '#064e3b',
  '#e9f7ee': '#064e3b',
  '#fef7e0': '#78350f',
  '#e8f0fe': '#1e3a5f',
  '#ebedf0': '#1e293b',
  '#f5f7fa': '#0f172a',
};

function resolveDark(obj) {
  if (typeof obj !== 'object' || obj === null) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string') {
      out[k] = DARK_REPLACEMENTS[v] || v;
    } else if (typeof v === 'number') {
      out[k] = v;
    } else if (v !== null && typeof v === 'object') {
      out[k] = v; // nested objects not deeply resolved
    } else {
      out[k] = v;
    }
  }
  return out;
}

const _STRUCTURAL_RAW = {
  cardIcon: (bg) => ({
    width: '44px',
    height: '44px',
    borderRadius: '14px',
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  statIcon: (bg) => ({
    width: '44px',
    height: '44px',
    borderRadius: '14px',
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  badge: (bg, color) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
    background: bg,
    color,
  }),
  tag: (bg, color) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    background: bg,
    color,
  }),
  loading: { textAlign: 'center', padding: '80px 0', color: '#9ca3af', fontSize: '16px' },
  wrapper: { maxWidth: '1240px', margin: '0 auto', padding: '32px 24px' },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '28px',
  },
  topBarLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  title: { fontSize: '24px', fontWeight: 700, color: '#111827' },
  subtitle: { fontSize: '14px', color: '#6b7280' },
  refreshBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    borderRadius: '12px',
    border: '1.5px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '28px',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  cardValue: { fontSize: '26px', fontWeight: 800, color: '#111827', lineHeight: 1.2 },
  cardLabel: { fontSize: '12px', color: '#9ca3af', fontWeight: 500, marginTop: '2px' },
  chartRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '20px',
    marginBottom: '28px',
  },
  section: {
    background: '#fff',
    borderRadius: '16px',
    padding: '22px 26px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '22px',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '18px' },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f9fafb',
    borderRadius: '10px',
    padding: '8px 14px',
    border: '1px solid #f3f4f6',
    width: '280px',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '13px',
    flex: 1,
    fontFamily: 'inherit',
    color: '#111827',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left',
    borderBottom: '2px solid #f3f4f6',
    padding: '12px 14px',
    color: '#9ca3af',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  td: {
    borderBottom: '1px solid #f9fafb',
    padding: '12px 14px',
    color: '#374151',
    verticalAlign: 'middle',
  },
  emptyRow: { textAlign: 'center', padding: '40px 14px', color: '#9ca3af', fontSize: '13px' },
  badgeGreen: {
    background: '#ecfdf5',
    color: '#059669',
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  badgeRed: {
    background: '#fef2f2',
    color: '#dc2626',
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  badgeGray: {
    background: '#f3f4f6',
    color: '#6b7280',
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  badgeOrange: {
    background: '#fffbeb',
    color: '#d97706',
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  badgePurple: {
    background: '#f5f3ff',
    color: '#7c3aed',
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  badgeBlue: {
    background: '#eff6ff',
    color: '#2563eb',
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  btnOutline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '7px 12px',
    borderRadius: '8px',
    border: '1.5px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
  },
  btnAdmin: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '7px 14px',
    borderRadius: '8px',
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  btnDanger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '7px 10px',
    borderRadius: '8px',
    border: '1.5px solid #fecaca',
    background: '#fff',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modalBox: {
    background: '#fff',
    borderRadius: '20px',
    padding: '32px',
    maxWidth: '520px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  modalTitle: { fontSize: '20px', fontWeight: 700, color: '#111827' },
  modalDesc: { fontSize: '14px', color: '#6b7280', marginBottom: '20px', lineHeight: 1.5 },
  formGroup: { marginBottom: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  formInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
  },
  formSelect: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
    cursor: 'pointer',
  },
  formLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: '6px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1.5px solid #f3f4f6',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 22px',
    borderRadius: '12px',
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
  },
  btnSm: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '5px 10px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  toast: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    padding: '12px 24px',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    zIndex: 3000,
    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
    animation: 'toastIn 0.3s ease',
  },
  bulkBar: {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#111827',
    borderRadius: '16px',
    padding: '12px 24px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
    zIndex: 2000,
    flexWrap: 'wrap',
  },
  bulkCount: { color: '#9ca3af', fontSize: '13px', fontWeight: 600, marginRight: '8px' },
  bulkBtnGreen: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    background: '#059669',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  bulkBtnOrange: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    background: '#d97706',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  bulkBtnRed: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    background: '#dc2626',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  bulkBtnGray: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    background: '#6b7280',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  cardTitle: {
    fontSize: '17px',
    fontWeight: 700,
    color: '#111827',
    marginBottom: '22px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  select: {
    padding: '8px 14px',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    fontSize: '13px',
    fontFamily: 'inherit',
    color: '#111827',
    outline: 'none',
    background: '#fff',
    cursor: 'pointer',
  },
  pagosCount: { fontSize: '13px', color: '#6b7280', fontWeight: 500 },
  btnVer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '7px 12px',
    borderRadius: '8px',
    border: '1.5px solid #e5e7eb',
    background: '#fff',
    color: '#2563eb',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  actions: { display: 'flex', gap: '8px' },
  motivoLabel: { fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' },
  motivoInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    minHeight: '80px',
    resize: 'vertical',
  },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
  btnCancel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 18px',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
  },
  btnRechazar: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 18px',
    borderRadius: '10px',
    border: 'none',
    background: '#dc2626',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
  },
  btnAprobar: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 18px',
    borderRadius: '10px',
    border: 'none',
    background: '#059669',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
  },
  count: { fontSize: '13px', color: '#6b7280', fontWeight: 500 },
  pill: {
    display: 'inline-block',
    padding: '3px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  resumen: {
    background: '#f9fafb',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '20px',
  },
  resRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 0',
    marginBottom: '2px',
  },
  resLabel: { fontSize: '12px', color: '#9ca3af', fontWeight: 500 },
  resVal: { fontSize: '13px', color: '#111827', fontWeight: 600 },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '20px',
  },
  subdomainBox: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 14px',
    borderRadius: '8px',
    background: '#eff6ff',
    marginBottom: '18px',
  },
  subdomainLabel: { fontSize: '11px', color: '#2563eb', fontWeight: 600 },
  subdomainValue: { fontSize: '12px', color: '#1e40af', fontWeight: 700 },
  skillModalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
    padding: '20px',
  },
  skillModal: {
    background: '#fff',
    borderRadius: '20px',
    padding: '32px',
    maxWidth: '560px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  skillModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  skillModalTitle: { fontSize: '18px', fontWeight: 700, color: '#111827' },
  skillModalClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '4px',
    borderRadius: '8px',
  },
  skillModalBody: { display: 'flex', flexDirection: 'column', gap: '16px' },
  skillModalTextarea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    minHeight: '120px',
    resize: 'vertical',
  },
  skillModalFooter: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1.5px solid #f3f4f6',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2500,
    padding: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '28px',
  },
  statCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statValue: { fontSize: '26px', fontWeight: 800, color: '#111827', lineHeight: 1.2 },
  statLabel: { fontSize: '12px', color: '#9ca3af', fontWeight: 500, marginTop: '2px' },
  noPlan: { textAlign: 'center', padding: '80px 0', color: '#9ca3af', fontSize: '16px' },
  btnSuccess: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 22px',
    borderRadius: '12px',
    border: 'none',
    background: '#059669',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
  },
};

const STRUCTURAL = new Proxy(_STRUCTURAL_RAW, {
  get(target, prop) {
    const val = target[prop];
    if (typeof val === 'function') return val;
    if (!isDark()) return val;
    if (typeof val === 'object' && val !== null) return resolveDark(val);
    return val;
  },
});

const handler = {
  get(_, prop) {
    if (prop in _STRUCTURAL_RAW) return STRUCTURAL[prop];
    try {
      return isDark()
        ? DARK_VALS[prop] || LIGHT_VALS[prop] || '#999'
        : LIGHT_VALS[prop] || DARK_VALS[prop] || '#999';
    } catch {
      return LIGHT_VALS[prop] || '#999';
    }
  },
};

const COL = new Proxy({}, handler);
const C = COL;

export { COL, C, LIGHT_VALS as LIGHT, DARK_VALS as DARK };
export default COL;
